import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';
import type { Column } from '../../components/ui/table';

@customElement('cadence-teachers-page')
export class CadenceTeachersPage extends LitElement {
  @state() private teachers: any[] = [];
  @state() private loading = true;
  @state() private showCreate = false;
  @state() private form = { first_name: '', last_name: '', email: '', pay_type: 'per_class', pay_rate: '', pay_currency: 'EUR' };
  @state() private formError = '';
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-4); }
    h1 { margin: 0; font-size: 1.6rem; }
    .form { display: flex; flex-direction: column; gap: var(--space-4); }
    .form-error { color: var(--color-danger); font-size: 0.85rem; margin: 0; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try { this.teachers = await api.get<any[]>('/teachers'); }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async create() {
    if (!this.form.first_name || !this.form.last_name || !this.form.email) { this.formError = t('teachers.form.nameEmailRequired'); return; }
    this.saving = true; this.formError = '';
    try {
      await api.post('/teachers', { ...this.form, pay_rate: this.form.pay_rate ? Number(this.form.pay_rate) : undefined });
      showToast(t('teachers.toast.created'), 'success');
      this.showCreate = false;
      this.form = { first_name: '', last_name: '', email: '', pay_type: 'per_class', pay_rate: '', pay_currency: 'EUR' };
      await this.load();
    } catch (e) { this.formError = e instanceof ApiError ? e.message : t('teachers.toast.failed'); }
    finally { this.saving = false; }
  }

  private columns: Column[] = [
    { key: 'last_name', label: t('teachers.columns.name'), sortable: true, render: (row) => html`${row.first_name} ${row.last_name}` },
    { key: 'email', label: t('teachers.columns.email'), sortable: true },
    { key: 'pay_type', label: t('teachers.columns.payType'), render: (row) => row.pay_type === 'monthly_salary' ? t('teachers.payType.monthlySalary') : t('teachers.payType.perClass') },
    { key: 'pay_rate', label: t('teachers.columns.payRate'), align: 'right', render: (row) => row.pay_rate != null ? `${row.pay_rate} ${row.pay_currency ?? ''}` : '—' },
  ];

  render() {
    return html`
      <div class="head">
        <h1>${t('teachers.list.title')}</h1>
        <cadence-button @click=${() => { this.showCreate = true; this.formError = ''; }}>${t('teachers.list.add')}</cadence-button>
      </div>
      <cadence-table clickable .columns=${this.columns} .rows=${this.teachers}
        @row-click=${(e: CustomEvent) => router.navigate(`/admin/teachers/${e.detail.id}`)}></cadence-table>

      <cadence-modal ?open=${this.showCreate} heading=${t('teachers.form.heading')} @close=${() => (this.showCreate = false)}>
        <div class="form">
          ${this.formError ? html`<p class="form-error" role="alert">${this.formError}</p>` : ''}
          <cadence-input label=${t('teachers.form.firstName')} .value=${this.form.first_name} @input=${(e: CustomEvent) => (this.form = { ...this.form, first_name: e.detail })}></cadence-input>
          <cadence-input label=${t('teachers.form.lastName')} .value=${this.form.last_name} @input=${(e: CustomEvent) => (this.form = { ...this.form, last_name: e.detail })}></cadence-input>
          <cadence-input label=${t('teachers.form.email')} type="email" .value=${this.form.email} @input=${(e: CustomEvent) => (this.form = { ...this.form, email: e.detail })}></cadence-input>
          <cadence-select label=${t('teachers.form.payType')} .value=${this.form.pay_type}
            .options=${[{ value: 'per_class', label: t('teachers.payType.perClass') }, { value: 'monthly_salary', label: t('teachers.payType.monthlySalary') }]}
            @change=${(e: CustomEvent) => (this.form = { ...this.form, pay_type: e.detail })}></cadence-select>
          <cadence-input label=${t('teachers.form.payRate')} type="number" .value=${this.form.pay_rate} @input=${(e: CustomEvent) => (this.form = { ...this.form, pay_rate: e.detail })}></cadence-input>
          <cadence-currency-select label=${t('teachers.form.currency')} .value=${this.form.pay_currency} @change=${(e: CustomEvent) => (this.form = { ...this.form, pay_currency: e.detail })}></cadence-currency-select>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.showCreate = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.create()}>${t('teachers.form.create')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
